import { oc } from "@orpc/contract";
import { z } from "zod";
import { lenientEnum } from "../../schemas/common";
import { InboundEmailSchema } from "../accounts-payable/ap-invoices.contract";

/**
 * AR Payments RPC Contract
 *
 * Provides endpoints for listing payments with filtering/pagination and fetching
 * payment detail including all invoice allocations (applied amounts, dates, invoice references).
 */

const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  message: "Must be a numeric string",
});

const UUIDSchema = z.uuid();

/**
 * Payment status — terminal-only.
 *
 * Per `docs/accounts-receivable/data-model.md`, Payment houses two
 * optimistic matches (remittance + bank); per-flow state lives on the side
 * objects and is derived from FKs/audit rows, not status.
 */
export const PaymentStatusSchema = z.enum(["active", "reversed", "voided"]);

/**
 * Payment method enum
 */
export const PaymentMethodSchema = z.enum([
  "check",
  "wire",
  "ach",
  "eft",
  "credit_card",
  "other",
]);

const PaymentStatusOutputSchema = lenientEnum(PaymentStatusSchema);
const PaymentMethodOutputSchema = lenientEnum(PaymentMethodSchema);

/**
 * Pagination schema
 */
export const ArPaymentPaginationSchema = z.object({
  total: z.int().nonnegative(),
  limit: z.int().positive(),
  offset: z.int().nonnegative(),
});

/**
 * List AR payments input schema
 */
export const ListArPaymentsInputSchema = z.object({
  customerId: z.array(UUIDSchema).optional(),
  status: z.array(PaymentStatusSchema).optional(),
  paymentDateFrom: z.string().optional(),
  paymentDateTo: z.string().optional(),
  paymentMethod: z.array(PaymentMethodSchema).optional(),
  paymentAmountMin: z.number().optional(),
  paymentAmountMax: z.number().optional(),
  search: z.string().optional(),
  sortBy: z.enum(["paymentDate", "paymentAmount", "payerName"]).optional(),
  sortDirection: z.enum(["asc", "desc"]).optional(),
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

/**
 * AR payment list item schema — minimal fields for the list view
 */
export const ArPaymentListItemSchema = z.object({
  id: UUIDSchema,
  referenceNumber: z.string().nullable(),
  paymentDate: z.string().nullable(),
  paymentAmount: DecimalStringSchema.nullable(),
  paymentMethod: PaymentMethodOutputSchema,
  status: PaymentStatusOutputSchema,
  currencyCode: z.string().nullable(),
  customerName: z.string().nullable(),
  /**
   * The customer's name as the source ERP renders it (Famous shows
   * `Acme Foods (ACM)`). Display-only — `customerName` stays the raw value so
   * sorting, filtering, and export are unaffected.
   */
  erpDisplayName: z.string().nullable(),
  customerId: UUIDSchema.nullable(),
  payerName: z.string().nullable(),
  applicationCount: z.int().nonnegative(),
});

/**
 * List AR payments response schema
 */
export const ListArPaymentsResponseSchema = z.object({
  payments: z.array(ArPaymentListItemSchema),
  pagination: ArPaymentPaginationSchema,
});

/**
 * Get AR payment input schema
 */
export const GetArPaymentInputSchema = z.object({
  id: UUIDSchema,
});

/**
 * Invoice summary inside a payment application
 */
export const ArPaymentInvoiceSummarySchema = z.object({
  id: UUIDSchema,
  invoiceNumber: z.string().nullable(),
  invoiceDate: z.string(),
  netAmount: DecimalStringSchema.nullable(),
  balanceDue: DecimalStringSchema.nullable(),
  customerName: z.string().nullable(),
  status: z.string(),
});

/**
 * Payment application entry inside payment detail
 */
export const ArPaymentApplicationEntrySchema = z.object({
  id: UUIDSchema,
  invoiceId: UUIDSchema,
  appliedAmount: DecimalStringSchema.nullable(),
  applicationDate: z.string().nullable(),
  sequenceNumber: z.number().nullable(),
  /**
   * How the allocation was created. Only `"user"` allocations (manual entry)
   * can be removed from the UI; others are managed by their own flows.
   */
  allocationSource: z.string().nullable(),
  invoice: ArPaymentInvoiceSummarySchema.nullable(),
});

/**
 * Pending-review line item: a customer-claimed invoice match that hasn't
 * yet been approved (no payment_application materialized) or rejected.
 */
export const ArPendingReviewLineItemSchema = z.object({
  id: UUIDSchema,
  remittanceAdviceId: UUIDSchema,
  rawInvoiceRef: z.string(),
  invoiceId: UUIDSchema.nullable(),
  statedAmount: DecimalStringSchema.nullable(),
  matchStatus: z.enum([
    "unmatched",
    "auto_matched",
    "matched",
    "ambiguous",
    "rejected",
  ]),
  matchReason: z.string().nullable(),
  invoice: ArPaymentInvoiceSummarySchema.nullable(),
});

/**
 * Bank-side cash event linked to this payment. Present when the payment has
 * been reconciled against a `ar_bank_transactions` row (Famous BANK_ACTIVITY,
 * Plaid transaction, etc.).
 */
export const ArPaymentBankTransactionSchema = z.object({
  id: UUIDSchema,
  transactionDate: z.string().nullable(),
  amount: DecimalStringSchema.nullable(),
  originSystem: z.string().nullable(),
  merchantName: z.string().nullable(),
});

/**
 * Remittance advice summary linked to this payment. A payment can have zero,
 * one, or many remittance advices (e.g., a single check covering invoices
 * announced across multiple remittance emails).
 *
 * Channel enum is duplicated from `ar-remittances.contract.ts` to avoid a
 * circular contract import — the source-of-truth lives there.
 */
export const ArPaymentLinkedRemittanceSchema = z.object({
  id: UUIDSchema,
  channel: z.enum(["email", "edi", "portal", "manual"]),
  receivedAt: z.string(),
  statedTotalAmount: DecimalStringSchema.nullable(),
  statedReferenceNo: z.string().nullable(),
  statedCustomerName: z.string().nullable(),
});

/**
 * AR payment detail schema — full payment with all invoice allocations
 */
export const ArPaymentDetailSchema = ArPaymentListItemSchema.extend({
  description: z.string().nullable(),
  /**
   * Source system the payment came from. `"manual"` records were entered
   * through the UI and are the only ones the manual-entry endpoints allow to
   * be edited / voided; everything else is synced and read-only.
   */
  originSystem: z.string().nullable(),
  invoiceApplications: z.array(ArPaymentApplicationEntrySchema),
  pendingReviewLineItems: z.array(ArPendingReviewLineItemSchema),
  sourceEmail: InboundEmailSchema.nullable(),
  bankTransaction: ArPaymentBankTransactionSchema.nullable(),
  linkedRemittances: z.array(ArPaymentLinkedRemittanceSchema),
});

/**
 * Get AR payment response schema
 */
export const GetArPaymentResponseSchema = z.object({
  payment: ArPaymentDetailSchema.nullable(),
});

/**
 * Create AR payment input — editable fields for a manually-entered payment.
 * Origin is stamped server-side (originSystem="manual"); status defaults to
 * "active".
 */
export const CreateArPaymentInputSchema = z.object({
  customerId: UUIDSchema.nullable().optional(),
  payerName: z.string().trim().min(1).nullable().optional(),
  paymentDate: z.string().nullable().optional(),
  paymentAmount: DecimalStringSchema.nullable().optional(),
  paymentMethod: PaymentMethodSchema.nullable().optional(),
  referenceNumber: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  currencyCode: z.string().nullable().optional(),
});

export const CreateArPaymentResponseSchema = z.object({
  payment: ArPaymentDetailSchema,
});

/** Update is the same editable surface plus the target id. */
export const UpdateArPaymentInputSchema = CreateArPaymentInputSchema.extend({
  id: UUIDSchema,
});

export const UpdateArPaymentResponseSchema = z.object({
  payment: ArPaymentDetailSchema,
});

export const SetArPaymentStatusInputSchema = z.object({
  id: UUIDSchema,
  status: PaymentStatusSchema,
});

export const SetArPaymentStatusResponseSchema = z.object({
  payment: ArPaymentDetailSchema,
});

/**
 * AR Payments RPC Contract
 */
export const arPaymentsContracts = {
  list: oc
    .input(ListArPaymentsInputSchema)
    .output(ListArPaymentsResponseSchema)
    .route({ method: "GET" }),
  get: oc
    .input(GetArPaymentInputSchema)
    .output(GetArPaymentResponseSchema)
    .route({ method: "GET" }),
  create: oc
    .input(CreateArPaymentInputSchema)
    .output(CreateArPaymentResponseSchema)
    .route({ method: "POST" }),
  update: oc
    .input(UpdateArPaymentInputSchema)
    .output(UpdateArPaymentResponseSchema)
    .route({ method: "POST" }),
  setStatus: oc
    .input(SetArPaymentStatusInputSchema)
    .output(SetArPaymentStatusResponseSchema)
    .route({ method: "POST" }),
} as const;

// Type exports
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>;
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;
export type ArPaymentPagination = z.infer<typeof ArPaymentPaginationSchema>;
export type ListArPaymentsInput = z.infer<typeof ListArPaymentsInputSchema>;
export type ListArPaymentsResponse = z.infer<
  typeof ListArPaymentsResponseSchema
>;
export type ArPaymentListItem = z.infer<typeof ArPaymentListItemSchema>;
export type GetArPaymentInput = z.infer<typeof GetArPaymentInputSchema>;
export type GetArPaymentResponse = z.infer<typeof GetArPaymentResponseSchema>;
export type ArPaymentDetail = z.infer<typeof ArPaymentDetailSchema>;
export type ArPaymentInvoiceSummary = z.infer<
  typeof ArPaymentInvoiceSummarySchema
>;
export type ArPaymentApplicationEntry = z.infer<
  typeof ArPaymentApplicationEntrySchema
>;
export type ArPendingReviewLineItem = z.infer<
  typeof ArPendingReviewLineItemSchema
>;
export type ArPaymentLinkedRemittance = z.infer<
  typeof ArPaymentLinkedRemittanceSchema
>;
export type CreateArPaymentInput = z.infer<typeof CreateArPaymentInputSchema>;
export type CreateArPaymentResponse = z.infer<
  typeof CreateArPaymentResponseSchema
>;
export type UpdateArPaymentInput = z.infer<typeof UpdateArPaymentInputSchema>;
export type UpdateArPaymentResponse = z.infer<
  typeof UpdateArPaymentResponseSchema
>;
export type SetArPaymentStatusInput = z.infer<
  typeof SetArPaymentStatusInputSchema
>;
export type SetArPaymentStatusResponse = z.infer<
  typeof SetArPaymentStatusResponseSchema
>;
